import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {MultiSelect} from 'primereact/multiselect'
import {Tag} from 'primereact/tag'
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import DialogWrapper from '../new-table/DialogWrapper'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {sicknessLevelOptions} from '../summary/SummaryConstant'
import {useDatatableContext} from './DataTableContext'
import {
    filterEventTypes,
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterChangeListener,
    onFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'

const StyledFilterBySicknessLevel = forwardRef(({onFilter = () => {}}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateCommons, translateOptions} =
        TranslateUtils()

    const {user} = useAuth()
    const {tableType} = useDatatableContext()

    const panelVisible = useSignal(false)
    const selectedItems = useSignal([])
    const filterBySicknessLevelRef = useRef(null)

    const handleShowForm = () => {
        blockOverflownScroll()
        filterBySicknessLevelRef.current.onShow()
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        filterBySicknessLevelRef.current.onHide()
    }

    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value === null ? [] : e.value
    }

    const handleSubmit = () => {
        onFilterChangeListener(tableType, filterEventTypes.sicknessLevel, {
            sicknessLevels: selectedItems.value,
        })

        let filteredSelectedItems = []

        if (isArrayAndNotEmpty(selectedItems.value)) {
            filteredSelectedItems = sicknessLevelOptions?.filter(item =>
                selectedItems.value.includes(item.value),
            )
        }

        onFilterExplainChangeListener(
            tableType,
            filterExplainEventTypes.SICKNESS_LEVEL_FILTER_EXPLAIN_CHANGE,
            filteredSelectedItems,
        )

        handleHideForm()
    }

    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }

    useEffect(() => {
        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_SICKNESS_LEVEL_FILTER,
            )}`,
            () => {
                selectedItems.value = []
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )

            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_SICKNESS_LEVEL_FILTER,
            )
        }
    }, [])

    return (
        <DialogWrapper
            header={translateDatatable('Filter by Sickness Level')}
            ref={filterBySicknessLevelRef}
            onSubmit={handleSubmit}>
            <p className="p-0 mb-3">
                {translateDatatable(
                    'Select multiple sickness levels to display the patient list according to the chosen sickness levels',
                )}
            </p>
            <MultiSelect
                className="w-full h-3rem p-inputtext p-0 font-bold"
                options={
                    isArrayAndNotEmpty(translateOptions(sicknessLevelOptions))
                        ? translateOptions(sicknessLevelOptions)
                        : []
                }
                placeholder={translateCommons('Select an option')}
                defaultValue={[]}
                maxSelectedLabels={3}
                display={selectedItems.value?.length < 4 ? 'chip' : 'comma'}
                value={selectedItems.value || []}
                onChange={handleChangeSelectedItems}
                itemTemplate={item => {
                    return (
                        <Tag
                            className="px-3 mx-3"
                            style={{
                                width: '180px',
                            }}
                            severity={
                                item.value === 'normal'
                                    ? 'success'
                                    : item.value === 'mild'
                                    ? 'info'
                                    : item.value === 'moderate'
                                    ? 'warning'
                                    : item.value === 'severe'
                                    ? 'danger'
                                    : ''
                            }
                            value={
                                item.value === 'normal'
                                    ? 'NORMAL'
                                    : item.value === 'mild'
                                    ? 'MILD'
                                    : item.value === 'moderate'
                                    ? 'MODERATE'
                                    : item.value === 'severe'
                                    ? 'SEVERE'
                                    : ''
                            }
                        />
                    )
                }}
                panelFooterTemplate={() =>
                    renderPanelFooterTemplate(selectedItems.value)
                }
                {...panelVisiblilityProps}
            />

            <p className="font-bold">
                {translateCommons('You have selected')}{' '}
                <span className="text-darkcyan">
                    {selectedItems.value?.length < 1
                        ? 'no'
                        : selectedItems.value?.length}{' '}
                    {translateCommons('sickness level(s)')}
                </span>{' '}
                {translateCommons('out of')}{' '}
                <span className="text-darkcyan">
                    {sicknessLevelOptions?.length}{' '}
                    {translateCommons('available sickness level(s)')}
                </span>
            </p>

            {selectedItems.value?.length > 0 && (
                <div>
                    <div className="flex flex-wrap align-items-center gap-1">
                        {selectedItems.value?.map(selectedItemId => {
                            const sicknessLevelItem = sicknessLevelOptions.find(
                                item => item.value === selectedItemId,
                            )

                            if (!sicknessLevelItem) {
                                return null
                            }

                            return (
                                <div
                                    key={sicknessLevelItem.value}
                                    className="flex align-items-center">
                                    <Tag
                                        className="px-3 mr-2"
                                        style={{
                                            width: '120px',
                                        }}
                                        severity={
                                            sicknessLevelItem.value === 'normal'
                                                ? 'success'
                                                : sicknessLevelItem.value ===
                                                  'mild'
                                                ? 'info'
                                                : sicknessLevelItem.value ===
                                                  'moderate'
                                                ? 'warning'
                                                : sicknessLevelItem.value ===
                                                  'severe'
                                                ? 'danger'
                                                : ''
                                        }
                                        value={
                                            sicknessLevelItem.value === 'normal'
                                                ? 'NORMAL'
                                                : sicknessLevelItem.value ===
                                                  'mild'
                                                ? 'MILD'
                                                : sicknessLevelItem.value ===
                                                  'moderate'
                                                ? 'MODERATE'
                                                : sicknessLevelItem.value ===
                                                  'severe'
                                                ? 'SEVERE'
                                                : ''
                                        }
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </DialogWrapper>
    )
})

export default StyledFilterBySicknessLevel
